import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { listenToCleaningGuestReservations } from "../../../../redux/cleaningReservationSlice";
import { listenToCleaningData } from "../../../../redux/cleaningSlice";
import logo from "../../../../assets/app/logo.png";

/* ── green tokens ── */
const G = {
  green:     "#16a34a",
  greenDark: "#14532d",
  greenMid:  "#22c55e",
  greenBg:   "#f0fdf4",
  greenBorder: "rgba(22,163,74,0.3)",
  grad: "linear-gradient(135deg,#4ade80 0%,#16a34a 40%,#14532d 100%)",
};

const euro = "€";

const CleaningReceipt: React.FC = () => {
  const { guestId, reservationId } = useParams<{
    guestId: string;
    reservationId: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const [expanded, setExpanded] = useState(false);

  const reservations = useSelector(
    (s: RootState) => s.cleaningReservations.guestReservations
  );
  const listing  = useSelector((s: RootState) => s.cleaning.currentListing);
  const allUsers = useSelector((s: RootState) => s.auth.allUsers);

  const booking   = reservations.find((r) => r.id === reservationId);
  const hostUser  = allUsers.find((u) => u.userId === booking?.hostId);
  const guestUser = allUsers.find((u) => u.userId === booking?.guestId);

  useEffect(() => {
    if (guestId) dispatch(listenToCleaningGuestReservations(guestId));
    if (booking?.listingId) dispatch(listenToCleaningData(booking.listingId));
    if (!allUsers.length) dispatch(fetchAllUsers());
  }, [dispatch, guestId, booking?.listingId, allUsers.length]);

  const getStatusLabel = (b: any): string => {
    if (b.isCancelled)            return "Cancelled";
    if (b.isRefunded)             return "Refunded";
    if (b.isConfirmed && b.isPaid) return "Confirmed & Paid";
    if (b.isConfirmed)            return "Confirmed";
    if (b.isOngoing)              return "Ongoing";
    if (b.isCompleted)            return "Completed";
    return "Pending";
  };

  if (!booking) {
    return (
      <div style={S.loading}>
        <div style={S.loadingSpinner} />
        <p style={{ color: G.green, fontWeight: 700, marginTop: 16 }}>
          Loading receipt…
        </p>
      </div>
    );
  }

  /* ── duration ── */
  const isPerHour = booking.rateType === "perHour";
  const durationValue = isPerHour
    ? booking.totalHours || 0
    : booking.reservedDuration || 1;

  const durationLabel = (() => {
    const rt = booking.rateType;
    if (rt === "perHour")  return `${durationValue} hour${durationValue !== 1 ? "s" : ""}`;
    if (rt === "perWeek")  return `${Math.ceil(durationValue / 7)} week(s)`;
    if (rt === "perMonth") return `${Math.ceil(durationValue / 30)} month(s)`;
    if (rt === "perYear")  return `${Math.ceil(durationValue / 365)} year(s)`;
    return `${durationValue} day${durationValue !== 1 ? "s" : ""}`;
  })();

  const hostName = hostUser
    ? `${hostUser.firstName || ""} ${hostUser.lastName || ""}`.trim()
    : "—";

  const locationLabel = [listing?.address?.city, listing?.address?.country]
    .filter(Boolean).join(", ");

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });

  const extrasTotal = booking.extras
    ? Object.values(booking.extras).reduce((s, v) => s + Number(v), 0)
    : 0;

  /* ── PDF ── */
  const fetchImageAsBase64 = (url: string): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d")!.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.onerror = reject;
      img.src = url;
    });

  const downloadPDF = async () => {
    const doc       = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const guestName = guestUser?.firstName || "Guest";

    /* green border */
    doc.setDrawColor(22, 163, 74);
    doc.setLineWidth(1.5);
    doc.roundedRect(8, 8, pageWidth - 16, pageHeight - 16, 10, 10, "S");

    /* dark green header */
    doc.setFillColor(20, 83, 45);
    doc.rect(0, 0, pageWidth, 32, "F");

    try {
      doc.addImage(logo, "PNG", 10, 5, 18, 18);
    } catch {}

    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text("ARELUXE", 32, 20);

    /* greeting */
    let y = 50;
    doc.setFontSize(18);
    doc.setTextColor(22, 163, 74);
    doc.setFont("helvetica", "bold");
    doc.text(`Hi ${guestName},`, pageWidth / 2, y, { align: "center" });

    y += 10;
    doc.setFontSize(11);
    doc.setTextColor(85, 85, 85);
    doc.setFont("helvetica", "normal");
    doc.text("Your cleaning booking is confirmed.", pageWidth / 2, y, { align: "center" });

    /* listing title */
    y += 12;
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(listing?.title || listing?.name || "Cleaning Service", pageWidth / 2, y, { align: "center" });

    if (locationLabel) {
      y += 8;
      doc.setFontSize(10);
      doc.setTextColor(120, 120, 120);
      doc.setFont("helvetica", "normal");
      doc.text(locationLabel, pageWidth / 2, y, { align: "center" });
    }

    /* listing photo */
    y += 8;
    const photo = listing?.photos?.[0];
    if (photo) {
      try {
        const imgData = await fetchImageAsBase64(photo);
        doc.addImage(imgData, "JPEG", 20, y, pageWidth - 40, 55, undefined, "FAST");
        y += 65;
      } catch { y += 6; }
    }

    /* dates box */
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.roundedRect(20, y, pageWidth - 40, 40, 5, 5, "S");

    const boxTop = y + 9;
    doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(22, 163, 74);
    doc.text("SERVICE DATE", 30, boxTop);
    doc.setFont("helvetica", "normal").setTextColor(80, 80, 80);
    doc.text(fmtDate(booking.checkIn), 30, boxTop + 8);

    doc.setFont("helvetica", "bold").setTextColor(22, 163, 74);
    doc.text("END DATE", pageWidth / 2 + 5, boxTop);
    doc.setFont("helvetica", "normal").setTextColor(80, 80, 80);
    doc.text(booking.checkOut ? fmtDate(booking.checkOut) : "Same day", pageWidth / 2 + 5, boxTop + 8);

    doc.setFont("helvetica", "bold").setTextColor(22, 163, 74);
    doc.text("DURATION", 30, boxTop + 20);
    doc.setFont("helvetica", "normal").setTextColor(80, 80, 80);
    doc.text(durationLabel, 30, boxTop + 28);

    if (booking.bedroomAmenityLabel) {
      doc.setFont("helvetica", "bold").setTextColor(22, 163, 74);
      doc.text("HOME SIZE", pageWidth / 2 + 5, boxTop + 20);
      doc.setFont("helvetica", "normal").setTextColor(80, 80, 80);
      doc.text(booking.bedroomAmenityLabel, pageWidth / 2 + 5, boxTop + 28);
    }

    y += 50;

    /* payment table */
    const rows: any[] = [
      ["Service", durationLabel, `€${Number(booking.baseServiceCost || 0).toFixed(2)}`],
    ];

    if (booking.extras) {
      Object.entries(booking.extras).forEach(([name, price]) => {
        rows.push([`Add-on: ${name}`, "—", `€${Number(price).toFixed(2)}`]);
      });
    }

    if (Number(booking.baseServiceCost) > Number(booking.discountedServiceCost)) {
      rows.push(["Discount", "—", `-€${(Number(booking.baseServiceCost) - Number(booking.discountedServiceCost)).toFixed(2)}`]);
    }

    rows.push(["Service fee", "—", `€${Number(booking.guestsFee || 0).toFixed(2)}`]);

    if (Number(booking.creditsUsed) > 0) {
      rows.push(["Credits applied", "—", `-€${Number(booking.creditsUsed).toFixed(2)}`]);
    }

    autoTable(doc, {
      startY: y,
      head: [["Item", "Detail", "Amount"]],
      body: rows,
      foot: [["", "TOTAL PAID", `€${Number(booking.totalPaid || 0).toFixed(2)}`]],
      headStyles:    { fillColor: [20, 83, 45], textColor: 255, fontStyle: "bold" },
      footStyles:    { fillColor: [240, 253, 244], textColor: [22, 163, 74], fontStyle: "bold", fontSize: 13 },
      alternateRowStyles: { fillColor: [240, 253, 244] },
      styles: { fontSize: 10, cellPadding: 5 },
    });

    doc.save("cleaning_receipt.pdf");
  };

  /* ── UI ── */
  const photo = listing?.photos?.[0] || logo;

  return (
    <div style={S.page}>
      <motion.div
        style={S.card}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        {/* ── HEADER ── */}
        <div style={S.header} onClick={() => setExpanded(!expanded)}>
          <div>
            <div style={S.headerLabel}>🧹 Cleaning Receipt</div>
            <div style={S.headerTitle}>
              {listing?.title || listing?.name || "Cleaning Service"}
            </div>
          </div>
          {expanded
            ? <ExpandLess style={{ color: G.green }} />
            : <ExpandMore style={{ color: G.green }} />
          }
        </div>

        {/* ── COVER PHOTO + TOTAL ── */}
        <div style={S.coverWrap}>
          <img src={photo} alt="" style={S.coverImg} />
          <div style={S.coverOverlay}>
            <div style={S.coverLabel}>Total Paid</div>
            <div style={S.coverAmount}>
              {euro}{Number(booking.totalPaid ?? 0).toFixed(2)}
            </div>
            <div style={S.coverStatus}>{getStatusLabel(booking)}</div>
          </div>
        </div>

        {/* ── DATES STRIP ── */}
        <div style={S.datesStrip}>
          <div style={S.dateCell}>
            <div style={S.dateLabel}>SERVICE DATE</div>
            <div style={S.dateValue}>{fmtDate(booking.checkIn)}</div>
          </div>
          <div style={{ width: 1, background: G.greenBorder }} />
          <div style={S.dateCell}>
            <div style={S.dateLabel}>END DATE</div>
            <div style={S.dateValue}>
              {booking.checkOut && booking.checkOut !== booking.checkIn
                ? fmtDate(booking.checkOut)
                : "Same day"}
            </div>
          </div>
          <div style={{ width: 1, background: G.greenBorder }} />
          <div style={S.dateCell}>
            <div style={S.dateLabel}>DURATION</div>
            <div style={S.dateValue}>{durationLabel}</div>
          </div>
        </div>

        {/* ── EXPANDABLE DETAILS ── */}
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: expanded ? 1 : 0, height: expanded ? "auto" : 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          style={S.whitePage}
        >
          <div style={S.pageTop} />
          <div style={S.pageContent}>

            {/* ── BOOKING INFO ── */}
            <div style={S.sectionHeader}>Booking info</div>

            {locationLabel && (
              <div style={S.row}>
                <span>Location</span>
                <span>{locationLabel}</span>
              </div>
            )}

            {booking.bedroomAmenityLabel && (
              <div style={S.row}>
                <span>Home size</span>
                <span>{booking.bedroomAmenityLabel}</span>
              </div>
            )}

            {booking.rateType && (
              <div style={S.row}>
                <span>Rate type</span>
                <span style={{ textTransform: "capitalize" }}>{booking.rateType}</span>
              </div>
            )}

            <div style={S.row}>
              <span>Status</span>
              <span style={{ color: G.green, fontWeight: 700 }}>
                {getStatusLabel(booking)}
              </span>
            </div>

            {/* ── PAYMENT BREAKDOWN ── */}
            <div style={S.sectionHeader}>Payment breakdown</div>

            <div style={S.row}>
              <span>Base service</span>
              <span>{euro}{Number(booking.baseServiceCost || 0).toFixed(2)}</span>
            </div>

            {/* extras */}
            {booking.extras && Object.keys(booking.extras).length > 0 && (
              <>
                <div style={{ ...S.sectionHeader, marginTop: 10 }}>Add-on services</div>
                {Object.entries(booking.extras).map(([name, price]) => (
                  <div key={name} style={S.row}>
                    <span>{name}</span>
                    <span>{euro}{Number(price).toFixed(2)}</span>
                  </div>
                ))}
                <div style={{ ...S.row, fontWeight: 700 }}>
                  <span>Extras total</span>
                  <span>{euro}{extrasTotal.toFixed(2)}</span>
                </div>
              </>
            )}

            {/* discount */}
            {Number(booking.baseServiceCost) > Number(booking.discountedServiceCost) && (
              <div style={{ ...S.row, color: G.green, fontWeight: 700 }}>
                <span>Discount</span>
                <span>
                  -{euro}{(
                    Number(booking.baseServiceCost) -
                    Number(booking.discountedServiceCost)
                  ).toFixed(2)}
                </span>
              </div>
            )}

            {/* service fee */}
            <div style={S.row}>
              <span>Service fee</span>
              <span>{euro}{Number(booking.guestsFee ?? 0).toFixed(2)}</span>
            </div>

            {/* credits */}
            {Number(booking.creditsUsed) > 0 && (
              <div style={{ ...S.row, color: G.green, fontWeight: 700 }}>
                <span>Credits applied</span>
                <span>-{euro}{Number(booking.creditsUsed).toFixed(2)}</span>
              </div>
            )}

            {/* total */}
            <div style={S.totalRow}>
              <span>Total Paid</span>
              <span style={{ color: G.green }}>
                {euro}{Number(booking.totalPaid ?? 0).toFixed(2)}
              </span>
            </div>

            {/* ── HOST ── */}
            <div style={S.sectionHeader}>Service provider</div>
            <div style={S.row}>
              <span>Name</span>
              <span>{hostName}</span>
            </div>
            {listing?.isCompany !== undefined && (
              <div style={S.row}>
                <span>Type</span>
                <span>{listing.isCompany ? "Professional Company" : "Individual Cleaner"}</span>
              </div>
            )}

            {/* ── DOWNLOAD ── */}
            <button onClick={downloadPDF} style={S.downloadBtn}>
              📥 Download PDF Receipt
            </button>

          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CleaningReceipt;

/* ── styles ── */
const S: Record<string, React.CSSProperties> = {
  page: {
    background: G.greenBg,
    minHeight: "100vh",
    padding: "40px 20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    fontFamily: "Poppins, sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: 22,
    border: `2px solid ${G.greenBorder}`,
    padding: 0,
    width: "95%",
    maxWidth: 480,
    boxShadow: "0 12px 40px rgba(22,163,74,0.15)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    padding: "20px 24px 16px",
    borderBottom: `1px solid ${G.greenBorder}`,
  },
  headerLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: G.green,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: G.greenDark,
  },
  coverWrap: {
    position: "relative",
    width: "100%",
    height: 200,
    overflow: "hidden",
  },
  coverImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  coverOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    background: "linear-gradient(transparent, rgba(20,83,45,0.92))",
    padding: "16px 20px",
  },
  coverLabel: {
    fontSize: 12,
    color: "#86efac",
    fontWeight: 600,
    marginBottom: 2,
  },
  coverAmount: {
    fontSize: 32,
    fontWeight: 900,
    color: "#fff",
    lineHeight: 1,
  },
  coverStatus: {
    fontSize: 12,
    color: "#4ade80",
    fontWeight: 700,
    marginTop: 4,
  },
  datesStrip: {
    display: "flex",
    borderBottom: `1px solid ${G.greenBorder}`,
  },
  dateCell: {
    flex: 1,
    padding: "12px 14px",
  },
  dateLabel: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: "0.1em",
    color: G.green,
    textTransform: "uppercase" as const,
    marginBottom: 4,
  },
  dateValue: {
    fontSize: 13,
    fontWeight: 700,
    color: G.greenDark,
  },
  whitePage: {
    background: "#fff",
    overflow: "hidden",
  },
  pageTop: {
    height: 4,
    background: `repeating-linear-gradient(90deg,${G.greenBorder} 0 10px,#fff 10px 20px)`,
  },
  pageContent: {
    padding: "16px 24px 24px",
    background: `repeating-linear-gradient(#fff,#fff 28px,${G.greenBg} 29px)`,
  },
  sectionHeader: {
    marginTop: 16,
    marginBottom: 8,
    fontWeight: 800,
    fontSize: 10,
    color: G.green,
    textTransform: "uppercase" as const,
    letterSpacing: "0.1em",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13,
    color: "#222",
    padding: "7px 0",
    borderBottom: `1px dashed ${G.greenBorder}`,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 16,
    fontWeight: 900,
    color: G.greenDark,
    padding: "12px 0 8px",
    marginTop: 8,
    borderTop: `2px solid ${G.greenBorder}`,
  },
  downloadBtn: {
    width: "100%",
    marginTop: 24,
    background: G.grad,
    border: "none",
    borderRadius: 40,
    color: "#fff",
    fontWeight: 800,
    fontSize: 15,
    padding: "14px 0",
    cursor: "pointer",
    boxShadow: "0 6px 20px rgba(22,163,74,0.35)",
  },
  loading: {
    background: G.greenBg,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
  loadingSpinner: {
    width: 40,
    height: 40,
    border: `4px solid ${G.greenBorder}`,
    borderTop: `4px solid ${G.green}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};